import { ReferralData, SendToCCPayload, DemographicsPayload, utmPayload } from '../types';

/**
 * Builds a SendToCC payload for a NEW record (no existing CC patient)
 * Includes ALL demographic fields
 */

export const CcFieldNames = {
  utm_source: 'Avacare Website - UTM Campaign Source',
  utm_medium: 'Avacare Website - UTM Campaign Medium',
  utm_campaign: 'Avacare Website - UTM Campaign Name',
  utm_term: 'Avacare Website - UTM Campaign Term',
  utm_content: 'Avacare Website - UTM Campaign Content',
  sID: 'Avacare Website - sID',
  psecID: 'Avacare Website - psecID',
  atid: 'Avacare Website - UTM ATID',
  gclid: 'Avacare Website - UTM GCLID',
  fbclid: 'Avacare Website - UTM FBCLID',
} as const;

export const buildNewRecordPayload = (row: ReferralData, statusValue: string): SendToCCPayload => {
  let formattedPhone = row.phone;
  if (row.phone) {
    const digits = row.phone.replace(/\D/g, '');
    if (digits.length === 10) {
      formattedPhone = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
  }

  const demographics: DemographicsPayload = {
    firstName: row.firstName,
    lastName: row.lastName,
    dateOfBirth: row.dob,
    gender: row.gender,
    email: row.email,
    phone: formattedPhone,
    address: {
      street: row.address,
      city: row.city,
      state: row.state,
      zipCode: row.zipcode,
      stateCode: row.stateCode,
    },
    patientId: row.patientId,
  };

  const consent = (row.secondaryConsentinfo as any) || {};
  const consentPayload = {
    emailOptInDate: null,
    isSecondaryContactMediumEmail: consent.isSecondaryContactMediumEmail,
    isSecondaryContactMediumSMS: consent.isSecondaryContactMediumSMS,
    smsOptInDate: null,
  };

  const utm: utmPayload = {
    utm_source: row.utm_source,
    utm_medium: row.utm_medium,
    utm_campaign: row.utm_campaign,
    utm_term: row.utm_term,
    utm_content: row.utm_content,
    sID: row.sID,
    psecID: row.psecID,
    atid: row.atid,
    gclid: row.gclid,
    fbclid: row.fbclid,
  };
  return {
    ccId: null,
    guestRef: row.guestRef,
    enrollmentId: row.enrollmentId || null,
    leadId: row.leadId || 1,
    oldDedupStatus: row.dedupeStatus || null,
    newDedupStatus: statusValue,
    demographics,
    utm,
    consent: consentPayload,
    userId: localStorage.getItem('lma-userId') || '1',
  };
};

export const buildAutoMergePayload = (
  row: ReferralData,
  statusValue: string,
  enrichedDeduplicationData: any,
  ccPatientReferenceId: string
): SendToCCPayload => {
  const demographics: DemographicsPayload = {
    patientId: row.patientId,
  };

  if (!enrichedDeduplicationData?.firstName && row.firstName) {
    demographics.firstName = row.firstName;
  }

  if (!enrichedDeduplicationData?.lastName && row.lastName) {
    demographics.lastName = row.lastName;
  }

  if (!enrichedDeduplicationData?.dateOfBirth && row.dob) {
    demographics.dateOfBirth = row.dob;
  }

  if (!enrichedDeduplicationData?.primaryEmail?.email && row.email) {
    demographics.email = row.email;
  }

  if (!enrichedDeduplicationData?.phone1?.number && row.phone) {
    if (row.phone) {
      const digits = row.phone.replace(/\D/g, '');
      if (digits.length === 10) {
        demographics.phone = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      } else {
        demographics.phone = row.phone;
      }
    }
  }

  const needsStreet = !enrichedDeduplicationData?.address1;
  const needsCity = !enrichedDeduplicationData?.city;
  const needsState = !enrichedDeduplicationData?.state;
  const needsStateCode = !enrichedDeduplicationData?.stateCode;
  const needsZipCode = !enrichedDeduplicationData?.zip;
  const needsGender = !enrichedDeduplicationData?.genderCode;

  if (needsStreet || needsCity || needsState || needsStateCode || needsZipCode || needsGender) {
    demographics.address = {};
    if (needsStreet && row.address) {
      demographics.address.street = row.address;
    }
    if (needsCity && row.city) {
      demographics.address.city = row.city;
    }
    if (needsState && row.state) {
      demographics.address.state = row.state;
    }
    if (needsStateCode && row.stateCode) {
      demographics.address.stateCode = row.stateCode;
    }
    if (needsZipCode && row.zipcode) {
      demographics.address.zipCode = row.zipcode;
    }
    if (needsGender && row.gender) {
      demographics.gender = row.gender;
    }
  }

  const hasCustomField = (fieldName: string): boolean => {
    if (
      !enrichedDeduplicationData?.customFields ||
      !Array.isArray(enrichedDeduplicationData.customFields)
    ) {
      return false;
    }
    return enrichedDeduplicationData.customFields.some((field: any) => field.name === fieldName);
  };

  const utm: utmPayload = {};

  if (hasCustomField(CcFieldNames.utm_source) && row.utm_source) {
    const existingField = enrichedDeduplicationData.customFields.find(
      (field: any) => field.name === CcFieldNames.utm_source
    );
    utm.utm_source = existingField?.value
      ? `${existingField.value}, ${row.utm_source}`
      : row.utm_source;
  }
  if (hasCustomField(CcFieldNames.utm_medium) && row.utm_medium) {
    const existingField = enrichedDeduplicationData.customFields.find(
      (field: any) => field.name === CcFieldNames.utm_medium
    );
    utm.utm_medium = existingField?.value
      ? `${existingField.value}, ${row.utm_medium}`
      : row.utm_medium;
  }
  if (hasCustomField(CcFieldNames.utm_campaign) && row.utm_campaign) {
    const existingField = enrichedDeduplicationData.customFields.find(
      (field: any) => field.name === CcFieldNames.utm_campaign
    );
    utm.utm_campaign = existingField?.value
      ? `${existingField.value}, ${row.utm_campaign}`
      : row.utm_campaign;
  }
  if (hasCustomField(CcFieldNames.utm_term) && row.utm_term) {
    const existingField = enrichedDeduplicationData.customFields.find(
      (field: any) => field.name === CcFieldNames.utm_term
    );
    utm.utm_term = existingField?.value ? `${existingField.value}, ${row.utm_term}` : row.utm_term;
  }
  if (hasCustomField(CcFieldNames.utm_content) && row.utm_content) {
    const existingField = enrichedDeduplicationData.customFields.find(
      (field: any) => field.name === CcFieldNames.utm_content
    );
    utm.utm_content = existingField?.value
      ? `${existingField.value}, ${row.utm_content}`
      : row.utm_content;
  }
  if (hasCustomField(CcFieldNames.sID) && row.sID) {
    const existingField = enrichedDeduplicationData.customFields.find(
      (field: any) => field.name === CcFieldNames.sID
    );
    utm.sID = existingField?.value ? `${existingField.value}, ${row.sID}` : row.sID;
  }
  if (hasCustomField(CcFieldNames.psecID) && row.psecID) {
    const existingField = enrichedDeduplicationData.customFields.find(
      (field: any) => field.name === CcFieldNames.psecID
    );
    utm.psecID = existingField?.value ? `${existingField.value}, ${row.psecID}` : row.psecID;
  }
  if (hasCustomField(CcFieldNames.atid) && row.atid) {
    const existingField = enrichedDeduplicationData.customFields.find(
      (field: any) => field.name === CcFieldNames.atid
    );
    utm.atid = existingField?.value ? `${existingField.value}, ${row.atid}` : row.atid;
  }
  if (hasCustomField(CcFieldNames.gclid) && row.gclid) {
    const existingField = enrichedDeduplicationData.customFields.find(
      (field: any) => field.name === CcFieldNames.gclid
    );
    utm.gclid = existingField?.value ? `${existingField.value}, ${row.gclid}` : row.gclid;
  }
  if (hasCustomField(CcFieldNames.fbclid) && row.fbclid) {
    const existingField = enrichedDeduplicationData.customFields.find(
      (field: any) => field.name === CcFieldNames.fbclid
    );
    utm.fbclid = existingField?.value ? `${existingField.value}, ${row.fbclid}` : row.fbclid;
  }

  const consent = (row.secondaryConsentinfo as any) || {};
  const consentPayload = {
    emailOptInDate: null,
    isSecondaryContactMediumEmail: consent.isSecondaryContactMediumEmail,
    isSecondaryContactMediumSMS: consent.isSecondaryContactMediumSMS,
    smsOptInDate: null,
  };

  return {
    ccId: ccPatientReferenceId,
    guestRef: row.guestRef,
    enrollmentId: row.enrollmentId || null,
    leadId: row.leadId || 1,
    oldDedupStatus: row.dedupeStatus || null,
    newDedupStatus: statusValue,
    demographics,
    consent: consentPayload,
    utm,
    userId: localStorage.getItem('lma-userId') || '1',
  };
};

/**
 * Builds a SendToCC payload for a MERGE record
 * Only includes fields the user selected to override
 *
 * @param row - The source referral data
 * @param ccPatientId - The existing CC patient ID to merge with
 * @param selectedFields - Array of field keys the user chose to override
 * @param statusValue - The new dedupe status value
 */
export const buildMergeRecordPayloadBuilder = (
  row: ReferralData,
  ccPatientId: string,
  selectedFields: string[],
  statusValue: string
): SendToCCPayload => {
  const demographics: DemographicsPayload = {
    patientId: row.patientId,
  };
  const utm: utmPayload = {};

  const fieldMapping: Record<string, (value: any) => void> = {
    firstName: (v) => (demographics.firstName = v),
    lastName: (v) => (demographics.lastName = v),
    dob: (v) => (demographics.dateOfBirth = v),
    dateOfBirth: (v) => (demographics.dateOfBirth = v),
    gender: (v) => (demographics.gender = v),
    email: (v) => (demographics.email = v),
    phoneNumber: () => {
      const phoneValue = row.phone;
      if (phoneValue) {
        const digits = phoneValue.replace(/\D/g, '');
        if (digits.length === 10) {
          demographics.phone = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
        } else {
          demographics.phone = phoneValue;
        }
      }
    },
    address: (v) => {
      if (!demographics.address) demographics.address = {};
      demographics.address.street = v;
    },
    city: (v) => {
      if (!demographics.address) demographics.address = {};
      demographics.address.city = v;
    },
    state: (v) => {
      if (!demographics.address) demographics.address = {};
      demographics.address.state = v;
      demographics.address.stateCode = row.stateCode;
    },
    zipcode: (v) => {
      if (!demographics.address) demographics.address = {};
      demographics.address.zipCode = v;
    },
    utm_source: (v) => (utm.utm_source = v),
    utm_medium: (v) => (utm.utm_medium = v),
    utm_campaign: (v) => (utm.utm_campaign = v),
    utm_term: (v) => (utm.utm_term = v),
    utm_content: (v) => (utm.utm_content = v),
    sID: (v) => (utm.sID = v),
    psecID: (v) => (utm.psecID = v),
    atid: (v) => (utm.atid = v),
    fbclid: (v) => (utm.fbclid = v),
    gclid: (v) => (utm.gclid = v),
  };
  // Only add fields that were selected
  selectedFields.forEach((fieldKey) => {
    const mapper = fieldMapping[fieldKey];
    if (mapper) {
      if (fieldKey === 'phoneNumber') {
        mapper(null);
      } else {
        const value = (row as any)[fieldKey];
        if (value !== undefined) {
          mapper(value);
        }
      }
    }
  });

  const consent = (row.secondaryConsentinfo as any) || {};
  const consentPayload = {
    emailOptInDate: null,
    isSecondaryContactMediumEmail: consent.isSecondaryContactMediumEmail || '',
    isSecondaryContactMediumSMS: consent.isSecondaryContactMediumSMS || '',
    smsOptInDate: null,
  };

  return {
    ccId: ccPatientId,
    guestRef: row.guestRef,
    enrollmentId: row.enrollmentId || null,
    leadId: row.leadId || 1,
    oldDedupStatus: row.dedupeStatus || null,
    newDedupStatus: statusValue,
    demographics,
    consent: consentPayload,
    utm,
    userId: localStorage.getItem('lma-userId') || '1',
  };
};
